import { getHydrationPoint } from '@kurtosys/app-loader/dist/hydration.js';
import { common } from '@kurtosys/ksys-app-template';
import { ITranslation } from '@kurtosys/types/api/dictionary/index.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { ITranslations } from '@kurtosys/types/common/ITranslations.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStylesBase } from '../models/IComponentStylesBase.js';
import { IConfigurationBase } from '../models/IConfigurationBase.js';

import { AppStoreBase } from './base/AppStoreBase.js';
import { StoreBase } from './base/StoreBase.js';
import { KurtosysApiStore } from './KurtosysApiStore.js';
import { StoreContext } from './StoreContext.js';

export class TranslationStore<
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> extends StoreBase<TConfiguration, TComponentStyles> {
	@observable cultureRaw?: string = '';
	constructor(storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@action
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async initialize(): Promise<void> {}

	@observable.ref translationRecords: ITranslation[] = [];
	@computed
	get translationHelper(): common.helpers.TranslationHelper {
		return new common.helpers.TranslationHelper(
			this.targetTranslations,
			this.fallbackTranslations,
			this.manifest.defaultTranslations,
		);
	}

	@computed
	get appConfiguration() {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		return appStore?.configuration;
	}

	@computed
	get appParamsHelper() {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		return appStore?.appParamsHelper;
	}

	@computed
	get baseCulture() {
		let baseCulture = 'Default';
		const appConfig = this.appConfiguration;
		if (appConfig && appConfig.culture && appConfig.culture.base) {
			baseCulture = appConfig.culture.base;
		}
		return baseCulture;
	}

	@computed
	get defaultCulture() {
		let defaultCulture = 'en-GB';
		const appConfig = this.appConfiguration;
		if (appConfig && appConfig.culture && appConfig.culture.default) {
			defaultCulture = appConfig.culture.default;
		}
		return defaultCulture;
	}

	@computed
	get culture() {
		if (this.cultureRaw) {
			return this.cultureRaw;
		}
		let culture = this.defaultCulture;
		const appParams = this.appParamsHelper;
		if (appParams && appParams.inputs && appParams.inputs.culture) {
			culture = appParams.inputs.culture;
		}
		return culture
			.split('-')
			.map((segment, index) => (index === 0 ? segment.toLowerCase() : segment.toUpperCase()))
			.join('-');
	}

	async setCulture(value: string) {
		await this.loadTranslations(value);
		runInAction(() => {
			this.cultureRaw = value;
		});
	}

	// Builds a translation dictionary object for the current culture from all translation data. E.g:
	// { 'phrase': 'translation', 'phrase': 'translation', etc }
	@computed
	get targetTranslations() {
		return this.getTranslationsDictionary(this.translationRecords, this.culture);
	}

	@computed
	get fallbackTranslations() {
		return this.getTranslationsDictionary(this.translationRecords, this.defaultCulture);
	}

	getTranslationsDictionary(translationRecords: ITranslation[], targetCulture: string) {
		const response: ITranslations = translationRecords
			.filter((translationRecord) => {
				return (
					translationRecord.translationCulture === targetCulture &&
					translationRecord.culture === this.baseCulture
				);
			})
			.reduce((acc: ITranslations, translationRecord) => {
				const { phrase, translation } = translationRecord;
				acc[phrase] = translation;
				return acc;
			}, {});

		return response;
	}

	/**
	 * Gets the translations of an culture from the api an makes them available to the apps
	 * @param {string} culture - This is to get the translations of the new culture before it is set as the observable,
	 * 							 to ensure that the new translations is available once the culture changes and causes a rerender
	 */
	async loadTranslations(culture?: string) {
		if (this.storeContext) {
			const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
			const kurtosysApiStore =
				this.storeContext.get<KurtosysApiStore<TConfiguration, TComponentStyles>>('kurtosysApiStore');
			const { coreConfigurations = {} } = appStore;
			const { hydration = {} } = coreConfigurations;
			const { ignoreTranslations: ignoreTranslationsRaw = false } = hydration;
			const ignoreTranslations = typeof ignoreTranslationsRaw !== 'boolean' || ignoreTranslationsRaw;
			const hydrationPoint = getHydrationPoint();
			if (!ignoreTranslations && hydrationPoint.translations && hydrationPoint.translations.length > 0) {
				runInAction(() => {
					this.translationRecords = hydrationPoint.translations;
				});
			} else {
				// Prefer spread however some apps seem to have build errors with the tsconfig.preset (Around the target of es5).
				// TODO: Update once we have figured out the problem. Not consistent across apps
				// const translationCulture = [...new Set([this.culture, this.defaultCulture])];
				const translationCulture = Array.from(new Set([culture ?? this.culture, this.defaultCulture]));
				const translationsResult = await kurtosysApiStore.listTranslations.execute({
					body: { translationCulture },
				});
				runInAction(() => {
					this.translationRecords = translationsResult;
				});
			}
		}
	}

	translate = (key: string, placeholders?: any) => {
		return this.translationHelper.translate(key, placeholders);
	};
}
